import React, { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";

import utsa1 from "../assets/offer-letters/utsa_1.jpg";
import utsa2 from "../assets/offer-letters/utsa_2.jpg";
import bristol from "../assets/offer-letters/bristol.jpg";
import trinity from "../assets/offer-letters/trinity.jpg";
import oregon from "../assets/offer-letters/oregon.jpg";
import boston from "../assets/offer-letters/boston.jpg";
import schiller from "../assets/offer-letters/schiller.jpg";
import nottingham from "../assets/offer-letters/nottingham.jpg";
import hague from "../assets/offer-letters/hague.jpg";
import into from "../assets/offer-letters/into.jpg";
import aau from "../assets/offer-letters/aau.jpg";
import doverbroecks from "../assets/offer-letters/doverbroecks.jpg";
import oncampus from "../assets/offer-letters/oncampus.jpg";
import asu from "../assets/offer-letters/asu.jpg";

// Pool of all available real letter images for cycling
const POOL = [nottingham, hague, schiller, utsa1, bristol, trinity, utsa2, oregon, boston, into, aau, doverbroecks, oncampus, asu];
const pick = (i) => POOL[i % POOL.length];

const LETTERS = [
  { img: nottingham,   name: "Nottingham Trent University, UK" },
  { img: hague,        name: "The Hague University, Netherlands" },
  { img: pick(2),      name: "University of Europe, Germany" },
  { img: asu,          name: "Arizona State University, USA" },
  { img: utsa1,        name: "University of Texas at San Antonio, USA" },
  { img: bristol,      name: "University of Bristol, UK" },
  { img: trinity,      name: "Trinity College Dublin, Ireland" },
  { img: utsa2,        name: "University of Texas at San Antonio, USA" },
  { img: oregon,       name: "Oregon State University, USA" },
  { img: into,         name: "INTO London, UK" },
  { img: aau,          name: "Anglo-American University, Czech Republic" },
  { img: doverbroecks, name: "D\u2019Overbroeck\u2019s Oxford (Nord Anglia), UK" },
  { img: oncampus,     name: "OnCampus Ireland" },
  { img: asu,          name: "Arizona State University, USA" },
  { img: utsa1,        name: "University of Texas at San Antonio, USA" },
  { img: pick(15),     name: "Franklin Pierce University, USA" },
  { img: pick(16),     name: "Florida International University, USA" },
  { img: boston,       name: "Boston University, USA" },
  { img: schiller,     name: "Schiller International University, Spain" }
];

const I18N = {
  ru: { kicker: "ПОДТВЕРЖДЁННЫЕ ЗАЧИСЛЕНИЯ", title: "Письма о зачислении наших студентов", subtitle: "Реальные оферы от университетов по всему миру" },
  uk: { kicker: "ПІДТВЕРДЖЕНІ ЗАРАХУВАННЯ", title: "Листи про зарахування наших студентів", subtitle: "Реальні офери від університетів по всьому світу" },
  en: { kicker: "CONFIRMED ADMISSIONS", title: "Admission letters of our students", subtitle: "Real offers from universities around the world" },
  ar: { kicker: "قبولات مؤكدة", title: "خطابات قبول طلابنا", subtitle: "عروض حقيقية من جامعات حول العالم" },
  it: { kicker: "AMMISSIONI CONFERMATE", title: "Lettere di ammissione dei nostri studenti", subtitle: "Offerte reali da università di tutto il mondo" },
  zh: { kicker: "确认录取", title: "我们学生的录取通知书", subtitle: "来自全球大学的真实录取" },
  es: { kicker: "ADMISIONES CONFIRMADAS", title: "Cartas de admisión de nuestros estudiantes", subtitle: "Ofertas reales de universidades de todo el mundo" },
  pl: { kicker: "POTWIERDZONE PRZYJĘCIA", title: "Listy przyjęcia naszych studentów", subtitle: "Realne oferty z uczelni z całego świata" }
};

const OfferLetters = () => {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.uk;
  const isRtl = lang === 'ar';
  const carouselRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let raf;
    const tick = () => {
      if (!paused && el) {
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = el.scrollLeft - el.scrollWidth / 2;
        }
        el.scrollLeft += 0.5;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  const scroll = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const items = [...LETTERS, ...LETTERS];

  return (
    <section id="offer-letters" className="py-20 md:py-28 section-soft overflow-hidden" dir={isRtl ? 'rtl' : 'ltr'} data-testid="offer-letters-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: '#C9A96E' }}>{t.kicker}</div>
          <h2 className="headline-huge text-3xl md:text-5xl text-balance" style={{ color: '#1B2A4A' }}>{t.title}</h2>
          <p className="mt-4 text-base md:text-lg" style={{ color: '#666' }}>{t.subtitle}</p>
        </div>

        <div className="flex justify-end gap-3 mb-5">
          <button onClick={() => scroll(-1)} aria-label="prev" className="w-11 h-11 rounded-full flex items-center justify-center transition hover:opacity-80" style={{ background: 'transparent', border: '1.5px solid #C9A96E', color: '#C9A96E' }} data-testid="letters-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => scroll(1)} aria-label="next" className="w-11 h-11 rounded-full flex items-center justify-center transition hover:opacity-80" style={{ background: 'transparent', border: '1.5px solid #C9A96E', color: '#C9A96E' }} data-testid="letters-next">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={carouselRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        className="flex gap-4 overflow-x-auto hide-scrollbar px-5 md:px-8 pb-4"
        style={{ scrollbarWidth: 'none', cursor: 'grab' }}
        data-testid="letters-carousel"
      >
        {items.map((letter, i) => (
          <div
            key={i}
            className="shrink-0 rounded-xl overflow-hidden bg-white transition hover:-translate-y-1 hover:shadow-2xl"
            style={{
              width: '280px',
              height: '380px',
              border: '1px solid #C9A96E',
              boxShadow: '0 4px 18px rgba(27,42,74,0.08)'
            }}
            data-testid={`offer-letter-${i}`}
          >
            <div className="relative w-full" style={{ height: '320px', background: '#fff' }}>
              <img
                src={letter.img}
                alt={letter.name}
                className="w-full h-full select-none"
                style={{ objectFit: 'cover', objectPosition: 'top' }}
                draggable={false}
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12" style={{ background: 'linear-gradient(to top, #fff, transparent)' }} />
            </div>
            <div className="px-4 py-3 text-center" style={{ background: '#fff', borderTop: '1px solid rgba(201,169,110,0.25)' }}>
              <div className="text-sm font-semibold leading-snug" style={{ color: '#1B2A4A' }}>{letter.name}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default OfferLetters;
